// src/pages/Profile/ProfilePage.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import authStore from "../../utils/authStorage";
import "../../styles/Profile.css";
import "../../styles/Form.css";

export default function ProfilePage() {
  const [form, setForm] = useState({
    first_name: "",
    mid_name: "",
    last_name: "",
    gender: "",
    email: "",
    mobile: "",
    home_phone: "",
    dob: "",
    refer_status: "No",
    refer_by_name: "",
    refer_by_id: "",
    userid_no: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
    password: "",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);

  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
  const token = useMemo(() => localStorage.getItem("token"), []);
  const userId = useMemo(() => localStorage.getItem("userId"), []);
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  // Load profile
  useEffect(() => {
    (async () => {
      try {
        if (!token) {
          navigate("/login", { replace: true });
          return;
        }
        const { data } = await axios.get(
          `${BASE_URL}/api/candidate/user/profile`,
          { headers: authHeader }
        );
        setForm((f) => ({
          ...f,
          ...data,
          dob: data.dob ? String(data.dob).split("T")[0] : "",
          password: "",
        }));
        authStore.me = data;
        setConfirmPassword("");
      } catch (err) {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          authStore.clearAll();
          navigate("/login", { replace: true });
        } else {
          alert("Failed to load profile");
        }
      }
    })();
  }, [BASE_URL, navigate, token, authHeader]);

  // Load countries
  // Load countries
  useEffect(() => {
    async function fetchCountries() {
      try {
        const { data } = await axios.get(`${BASE_URL}/api/dropdowns/countries`);
        // ✅ Normalize response
        const list = Array.isArray(data) ? data : data?.countries || [];
        setCountries(list);
      } catch (err) {
        console.error("❌ Failed to load countries:", err);
        setCountries([]);
      }
    }
    fetchCountries();
  }, [BASE_URL]);

  // Save profile
  async function handleSubmit(e) {
    e.preventDefault();

    if (form.password || confirmPassword) {
      if (form.password !== confirmPassword) {
        alert("Passwords do not match");
        return;
      }
      if (form.password.length < 8) {
        alert("Password must be at least 8 characters");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name || null,
        mid_name: form.mid_name || null,
        last_name: form.last_name || null,
        gender: form.gender || null,
        email: form.email || null,
        mobile: form.mobile || null,
        home_phone: form.home_phone || null,
        dob: form.dob || null,
        address1: form.address1 || null,
        address2: form.address2 || null,
        city: form.city || null,
        province: form.province || null,
        country: form.country || null,
        postal_code: form.postal_code || null,
        ...(form.password ? { password: form.password } : {}),
      };

      await axios.put(`${BASE_URL}/api/candidate/users/${userId}`, payload, {
        headers: authHeader,
      });

      const { data: updatedProfile } = await axios.get(
        `${BASE_URL}/api/candidate/user/profile`,
        { headers: authHeader }
      );
      authStore.me = updatedProfile;
      window.dispatchEvent(new Event("profileUpdated"));

      setForm((f) => ({ ...f, password: "" }));
      setConfirmPassword("");

      alert("✔️ Profile saved successfully!");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        authStore.clearAll();
        navigate("/login", { replace: true });
      } else {
        alert(err?.response?.data?.message || "Failed to update profile");
      }
    } finally {
      setSaving(false);
    }
  }

  function handleReturn() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  }

  return (
    <div className="form-wrapper profile-page">
      <div className="profile-page-header">
        <h2>My Profile</h2>
        <button
          type="button"
          className="btn btn-outline-secondary profile-return-btn"
          onClick={handleReturn}
        >
          Return
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* PERSONAL INFO */}
        <fieldset className="border p-3 mb-4">
          <div className="profile-banner">
            <h3>Personal Information</h3>
          </div>

          <div className="row mb-3">
            <div className="col-md-3">
              <label>First Name</label>
              <input
                name="first_name"
                value={form.first_name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, first_name: e.target.value }))
                }
                className="form-control"
                required
              />
            </div>
            <div className="col-md-3">
              <label>Middle Name</label>
              <input
                name="mid_name"
                value={form.mid_name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, mid_name: e.target.value }))
                }
                className="form-control"
              />
            </div>
            <div className="col-md-3">
              <label>Last Name</label>
              <input
                name="last_name"
                value={form.last_name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, last_name: e.target.value }))
                }
                className="form-control"
                required
              />
            </div>
            <div className="col-md-3">
              <label>Gender</label>
              <select
                name="gender"
                className="form-select"
                value={form.gender || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, gender: e.target.value }))
                }
              >
                <option value="">Select a gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Contact Info */}
          <div className="row mb-3">
            <div className="col-md-4">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, email: e.target.value }))
                }
                className="form-control"
                required
              />
            </div>
            <div className="col-md-4">
              <label>Cell Phone</label>
              <input
                type="tel"
                name="mobile"
                value={form.mobile}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, mobile: e.target.value }))
                }
                className="form-control"
              />
            </div>
            <div className="col-md-4">
              <label>Home Phone</label>
              <input
                type="tel"
                name="home_phone"
                value={form.home_phone}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, home_phone: e.target.value }))
                }
                className="form-control"
              />
            </div>
          </div>
        </fieldset>

        {/* ADDRESS */}
        <fieldset className="border p-3 mb-4">
          <legend className="w-auto px-2">Address</legend>

          <div className="row mb-3">
            <div className="col-md-3">
              <label>City</label>
              <input
                name="city"
                value={form.city}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, city: e.target.value }))
                }
                className="form-control"
              />
            </div>
            <div className="col-md-3">
              <label>Province</label>
              <input
                name="province"
                value={form.province}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, province: e.target.value }))
                }
                className="form-control"
              />
            </div>
            <div className="col-md-3">
              <label>Country</label>
              <select
                name="country"
                value={form.country}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, country: e.target.value }))
                }
                className="form-select"
              >
                <option value="">Select a country</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.country_name}>
                    {c.country_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label>Postal Code</label>
              <input
                name="postal_code"
                value={form.postal_code}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, postal_code: e.target.value }))
                }
                className="form-control"
              />
            </div>
          </div>
        </fieldset>

        {/* SECURITY */}
        <fieldset className="border p-3 mb-4">
          <legend className="w-auto px-2">Security</legend>
          <div className="mb-3">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, password: e.target.value }))
              }
              className="form-control"
              placeholder="Leave blank to keep current password"
            />
          </div>
          <div className="mb-3">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="form-control"
              placeholder="Re-enter new password"
            />
          </div>
        </fieldset>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </form>
    </div>
  );
}
